/**
 * A crew's WeChat group (24 October 2026). Jacky: "I also want a Emma announce in WeChat, (only available when a
 * channel is defined for this installer)". This is where the channel is defined: one group per crew, picked from the
 * chats EmmA is already in. The scope sheet offers "EmmA in WeChat" only while a group is linked here.
 */
import {useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Button} from '@aiw/ui';
import {api, authHeaders, client, fetchImpl} from '../lib/api';
import {env} from '../lib/env';
import {useT} from '../lib/i18n';
import {inputCls} from './job-bits';
import {Panel} from '../shell/Page';

type Chat = {chat_name: string; room_id: string | null; messages: number; last_at: string; linked_supplier_id?: string | null};
async function getJson<T>(path: string): Promise<T> {
  const r = await fetchImpl(`${env.apiBase}${path}`, {headers: await authHeaders()});
  const body = await r.json().catch(() => ({})) as {data?: T; error?: string};
  if (!r.ok || body.data === undefined) throw Error(body.error ?? `HTTP ${r.status}`);
  return body.data;
}

export function CrewWechat({partyId}: {partyId: string}) {
  const {t} = useT();
  const linked = useQuery({queryKey: ['crew-wechat', partyId], queryFn: () => getJson<{linked: {id: string; chat_name: string} | null}>(`/api/installers/${encodeURIComponent(partyId)}/wechat`)});
  const [picking, setPicking] = useState(false), [choice, setChoice] = useState(''), [busy, setBusy] = useState(false), [notice, setNotice] = useState('');
  const chats = useQuery({queryKey: ['wechat-chats'], enabled: picking, queryFn: () => getJson<Chat[]>('/api/supplier-channels/wechat')});
  async function run(command: string, args: Record<string, unknown>) {
    setBusy(true); setNotice('');
    try {
      const r = await api.propose(client, {command, args, idempotency_key: `${command}:${partyId}:${crypto.randomUUID()}`});
      if (r.status === 'executed') { setNotice(t('crewWechat.saved')); setPicking(false); await linked.refetch(); }
      else setNotice(t('crewWechat.failed', {message: r.status === 'pending' ? r.status : r.reason ?? r.status}));
    } catch (e) { setNotice(t('crewWechat.failed', {message: e instanceof Error ? e.message : String(e)})); } finally { setBusy(false); }
  }
  const current = linked.data?.linked ?? null;
  return <Panel title={t('crewWechat.title')}>
    <p className="mb-2 text-sm text-muted-foreground">{t('crewWechat.help')}</p>
    <p className="text-sm">{current ? t('crewWechat.linked', {group: current.chat_name}) : t('crewWechat.none')}</p>
    <div className="mt-2 flex flex-wrap gap-2">
      <Button size="sm" variant="outline" onClick={() => setPicking(!picking)}>{t('crewWechat.pick')}</Button>
      {current && <Button size="sm" variant="ghost" disabled={busy} onClick={() => void run('unlink_installer_wechat', {id: current.id, reason: 'Unlinked on the crew page'})}>{t('crewWechat.unlink')}</Button>}
    </div>
    {picking && <div className="mt-2 space-y-2">
      {chats.isPending ? <p className="text-sm">{t('crewWechat.loading')}</p> : <select className={inputCls} value={choice} onChange={(e) => setChoice(e.target.value)}>
        <option value="">—</option>
        {(chats.data ?? []).map((c) => <option key={c.chat_name} value={c.chat_name}>{c.chat_name}</option>)}
      </select>}
      <Button size="sm" disabled={!choice || busy} onClick={() => { const c = chats.data?.find((x) => x.chat_name === choice); void run('link_installer_wechat', {party_id: partyId, chat_name: choice, room_id: c?.room_id && /@chatroom$/.test(c.room_id) ? c.room_id : null, note: ''}); }}>{t('crewWechat.link')}</Button>
    </div>}
    {notice && <p role="status" className="mt-2 text-sm">{notice}</p>}
  </Panel>;
}
